import { useEffect, useRef, useState } from 'react';
import { MSK_DATA_STATS, AGE_SEGMENTS, WEEK_DAYS, MAJOR_SEGMENTS, EXPERIENCE_SEGMENTS } from '../data/recruitData';

// ─── Major (理系/文系) Donut Chart with inline labels ────────────────────────
const MajorDonutChart = ({ animated, size = 150 }: { animated: boolean; size?: number }) => {
  const strokeWidth = Math.round(size * 0.2);
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;

  const midAngles: number[] = [];
  let cum = 0;
  MAJOR_SEGMENTS.forEach((seg) => {
    midAngles.push(((cum + seg.value / 2) / 100) * 2 * Math.PI);
    cum += seg.value;
  });

  const toVisual = (angle: number) => {
    const svgX = cx + r * Math.cos(angle);
    const svgY = cy + r * Math.sin(angle);
    return { vx: svgY, vy: size - svgX };
  };

  cum = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EFEFEF" strokeWidth={strokeWidth} />
        {MAJOR_SEGMENTS.map((seg, i) => {
          const prev = cum;
          cum += seg.value;
          const dash = animated ? circ * (seg.value / 100) : 0;
          const gap = circ - dash;
          const offset = -(circ * (prev / 100));
          return (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }}
            />
          );
        })}
      </svg>

      {/* セグメント上のインラインラベル */}
      {MAJOR_SEGMENTS.map((seg, i) => {
        const { vx, vy } = toVisual(midAngles[i]);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: vx,
              top: vy,
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              opacity: animated ? 1 : 0,
              transition: `opacity 0.5s ease ${0.9 + i * 0.15}s`,
              lineHeight: 1.15,
            }}
          >
            <div style={{ fontSize: 8, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
              {seg.label}
            </div>
            <div style={{ fontSize: 7.5, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
              {seg.pct}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Experience (未経験/経験) Donut Chart with inline labels ─────────────────
const ExperienceDonutChart = ({ animated, size = 150 }: { animated: boolean; size?: number }) => {
  const strokeWidth = Math.round(size * 0.2);
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;

  const midAngles: number[] = [];
  let cum = 0;
  EXPERIENCE_SEGMENTS.forEach((seg) => {
    midAngles.push(((cum + seg.value / 2) / 100) * 2 * Math.PI);
    cum += seg.value;
  });

  const toVisual = (angle: number) => {
    const svgX = cx + r * Math.cos(angle);
    const svgY = cy + r * Math.sin(angle);
    return { vx: svgY, vy: size - svgX };
  };

  cum = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EFEFEF" strokeWidth={strokeWidth} />
        {EXPERIENCE_SEGMENTS.map((seg, i) => {
          const prev = cum;
          cum += seg.value;
          const dash = animated ? circ * (seg.value / 100) : 0;
          const gap = circ - dash;
          const offset = -(circ * (prev / 100));
          return (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }}
            />
          );
        })}
      </svg>
      {EXPERIENCE_SEGMENTS.map((seg, i) => {
        const { vx, vy } = toVisual(midAngles[i]);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: vx,
              top: vy,
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              opacity: animated ? 1 : 0,
              transition: `opacity 0.5s ease ${0.9 + i * 0.15}s`,
              lineHeight: 1.15,
            }}
          >
            <div style={{ fontSize: 8, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
              {seg.label}
            </div>
            <div style={{ fontSize: 7.5, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
              {seg.pct}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Age Donut Chart with inline labels ──────────────────────────────────────
const AgeDonutChart = ({ animated }: { animated: boolean }) => {
  const size = 150;
  const strokeWidth = 30;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;

  const midAngles: number[] = [];
  let cum = 0;
  AGE_SEGMENTS.forEach((seg) => {
    midAngles.push(((cum + seg.value / 2) / 100) * 2 * Math.PI);
    cum += seg.value;
  });

  const toVisual = (angle: number) => {
    const svgX = cx + r * Math.cos(angle);
    const svgY = cy + r * Math.sin(angle);
    return { vx: svgY, vy: size - svgX };
  };

  cum = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EFEFEF" strokeWidth={strokeWidth} />
        {AGE_SEGMENTS.map((seg, i) => {
          const prev = cum;
          cum += seg.value;
          const dash = animated ? circ * (seg.value / 100) : 0;
          const gap = circ - dash;
          const offset = -(circ * (prev / 100));
          return (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }}
            />
          );
        })}
      </svg>

      {AGE_SEGMENTS.map((seg, i) => {
        const { vx, vy } = toVisual(midAngles[i]);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: vx,
              top: vy,
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              opacity: animated ? 1 : 0,
              transition: `opacity 0.5s ease ${0.9 + i * 0.15}s`,
              lineHeight: 1.15,
            }}
          >
            <div style={{ fontSize: 8, fontWeight: 700, color: '#fff', whiteSpace: 'pre-line' }}>
              {seg.label}
            </div>
            <div style={{ fontSize: 7.5, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
              {seg.pct}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Count-up Hook ────────────────────────────────────────────────────────────
const useCountUp = (target: number, run: boolean, duration = 1200) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let start: number | null = null;
    const tick = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [run, target, duration]);
  return run ? val : 0;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const RecruitData = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const {
    employeeCount, gender, averageAge,
    overtime,
    holidayStyle, holidayDetail, holidayCount,
    turnoverRate, turnoverPeriod,
  } = MSK_DATA_STATS;

  const cntEmp      = useCountUp(employeeCount, visible);
  const cntAge      = useCountUp(averageAge,    visible);
  const cntHoliday  = useCountUp(holidayCount,  visible);
  const cntOvertime = useCountUp(overtime,      visible);
  const cntTurnover = useCountUp(turnoverRate,  visible, 1400);

  const card = 'bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(137,181,107,0.12)] transition-all duration-500 hover:-translate-y-1 group flex flex-col';
  const fadeUp = (delay: number) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(20px)',
    transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
  });

  return (
    <section className="py-20 bg-[#F8FAF6] overflow-hidden relative" id="data" ref={sectionRef}>
      <div
        className="absolute -top-4 right-0 font-extrabold text-[#89B56B]/[0.04] leading-none
                   select-none pointer-events-none tracking-tight"
        style={{ fontSize: 'clamp(100px, 18vw, 220px)' }}
      >
        DATA
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="text-center mb-14" style={fadeUp(0)}>
          <p className="text-[#89B56B] text-xs font-semibold tracking-[0.25em] uppercase mb-4">
            Data
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#89B56B]">
            データで見るMSK
          </h2>
        </div>

        {/* 1行目：3枚 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style={{ gridAutoRows: 'minmax(210px, auto)' }}>

          {/* 1. 社員数 */}
          <div className={`${card} md:col-span-2 lg:col-span-1`} style={fadeUp(60)}>
            <p className="text-[10px] font-semibold text-gray-400 tracking-[0.2em] uppercase mb-4">
              社員数
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="flex items-end gap-1.5 mb-4">
                  <span
                    className="font-extrabold leading-none"
                    style={{
                      fontSize: 'clamp(80px, 12vw, 108px)',
                      background: 'linear-gradient(135deg, #89B56B 0%, #89B56B 70%, #E8956B 70%, #E8956B 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      display: 'inline-block',
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: '2ch',
                      textAlign: 'right',
                      transform: visible ? 'scale(1)' : 'scale(0.6)',
                      opacity: visible ? 1 : 0,
                      transition: 'transform 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.1s, opacity 0.5s ease 0.1s',
                    }}
                  >
                    {cntEmp}
                  </span>
                  <span className="font-bold text-2xl" style={{ color: '#E8956B' }}>名</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#89B56B]"></span>
                    男性 {gender.male}%
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#E8956B]"></span>
                    女性 {gender.female}%
                  </span>
                </div>
              </div>
              <div
                className="flex-1 h-32 rounded-xl overflow-hidden bg-white"
                style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s ease 0.4s' }}
              >
                <img
                  src="/image/recruit-data1.jpg"
                  alt="社員チームイラスト"
                  className="w-full h-full object-contain object-center"
                />
              </div>
            </div>
          </div>

          {/* 2. 平均年齢 */}
          <div className={card} style={fadeUp(180)}>
            <p className="text-[10px] font-semibold text-gray-400 tracking-[0.2em] uppercase mb-3">
              平均年齢
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-end gap-1 flex-shrink-0">
                <span
                  className="font-extrabold text-[#89B56B] leading-none"
                  style={{ fontSize: 'clamp(80px, 12vw, 108px)' }}
                >
                  {cntAge}
                </span>
                <span className="text-2xl font-bold text-[#89B56B] mb-1">歳</span>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <AgeDonutChart animated={visible} />
              </div>
            </div>
          </div>

          {/* 3. 平均残業時間(月) */}
          <div className={card} style={fadeUp(240)}>
            <p className="text-[10px] font-semibold text-gray-400 tracking-[0.2em] uppercase mb-3">
              平均残業時間(月)
            </p>
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-shrink-0">
                <div className="flex flex-col items-end mb-3">
                  <span
                    className="font-extrabold text-[#89B56B] leading-none"
                    style={{
                      fontSize: 'clamp(80px, 12vw, 108px)',
                      fontVariantNumeric: 'tabular-nums',
                      display: 'inline-block',
                      minWidth: '2ch',
                      textAlign: 'right',
                    }}
                  >
                    {cntOvertime}
                  </span>
                  <span className="text-2xl font-bold text-[#89B56B] mt-1">時間以下</span>
                </div>
              </div>
              <div
                className="flex-1 h-28 rounded-xl overflow-hidden bg-[#89B56B]/10"
                style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s ease 0.4s' }}
              >
                <img
                  src="/image/recruit-data2.png"
                  alt="定時退社イメージ"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>

        </div>

        {/* 2行目：3枚 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" style={{ gridAutoRows: 'minmax(210px, auto)' }}>

          {/* 4. 年間休日 */}
          <div className={card} style={fadeUp(360)}>
            <p className="text-[10px] font-semibold text-gray-400 tracking-[0.2em] uppercase mb-3">
              年間休日
            </p>
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-sm font-extrabold text-[#89B56B] leading-tight">{holidayStyle}</p>
                <p className="text-xs text-gray-400 mt-0.5">{holidayDetail}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-end gap-0.5">
                  <span
                    className="font-extrabold text-[#89B56B] leading-none"
                    style={{ fontSize: 'clamp(60px, 9vw, 80px)' }}
                  >
                    {cntHoliday}
                  </span>
                  <span className="text-xl font-bold text-[#89B56B] mb-1.5">日</span>
                </div>
                <p className="text-[10px] text-gray-400">※前年度実績</p>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {WEEK_DAYS.map((d, i) => (
                <div
                  key={d}
                  className="h-9 rounded-lg flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: i >= 5 ? (visible ? '#89B56B' : '#E5E7EB') : '#F3F4F6',
                    color: i >= 5 ? '#fff' : '#9CA3AF',
                    transition: `background-color 0.4s ease ${500 + i * 60}ms`,
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
          </div>

          {/* 5. 離職率 */}
          <div
            className={`${card} relative overflow-hidden`}
            style={fadeUp(540)}
          >
            {/* 背景画像 - カード全体 */}
            <img
              src="/image/recruit-data3.png"
              alt="離職率背景"
              className="absolute object-contain object-center pointer-events-none"
              style={{ top: '43%', left: '50%', transform: 'translate(-50%, -50%)', width: '70%', height: '70%' }}
            />
            {/* コンテンツ */}
            <div className="relative z-10 flex flex-col flex-1">
              <p className="text-[10px] font-semibold text-gray-400 tracking-[0.2em] uppercase mb-4">
                離職率
              </p>
              <div
                className="flex flex-col items-center justify-center flex-1 gap-3 pt-8"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: 'opacity 0.5s ease 560ms',
                }}
              >
                <div className="flex items-end gap-1 leading-none">
                  <span
                    className="font-extrabold text-[#89B56B] leading-none"
                    style={{ fontSize: 'clamp(80px, 12vw, 108px)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {cntTurnover}
                  </span>
                  <span className="text-2xl font-bold text-[#89B56B] mb-2">%</span>
                </div>
                <p className="text-[11px] text-gray-400 tracking-wide">
                  集計期間：{turnoverPeriod}
                </p>
              </div>
            </div>
          </div>

          {/* 6. 理系/文系・未経験比率（2チャート合体カード） */}
          <div className={card} style={fadeUp(640)}>
            <p className="text-[10px] font-semibold text-gray-400 tracking-[0.2em] uppercase mb-3">
              文系・理系/未経験比率
            </p>
            <div className="flex items-center justify-around flex-1 gap-2">
              {/* 左：文系/理系 */}
              <div className="flex flex-col items-center">
                <MajorDonutChart animated={visible} size={118} />
              </div>

              {/* 右：未経験/経験 */}
              <div className="flex flex-col items-center">
                <ExperienceDonutChart animated={visible} size={118} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default RecruitData;
